import {
  readStateFromURL,
  writeStateToURL,
  saveBundleToStorage,
  readSavedBundles,
  deleteBundleFromStorage,
  generateBundleId,
  type URLProductRef,
  type SelectedProduct,
} from '../modules/bundle-state';
import { Dialog } from '../modules/dialog';
import { NameDialog } from '../modules/name-dialog';
import { ReplaceConfirmDialog } from '../modules/bundle-replace-guard';
import { addBundleToCart } from '../modules/bundle-cart-add';
import { trackEvent } from '../utils/analytics';
import { hydrateCollectionsFilterUI, peekCollectionFilterCard } from '../utils/collection-filter-hydration';
import {
  fetchStorefront,
  gidToNumericId,
  fetchVariantAvailability,
  sanitizeSearchQuery,
  CONNECTOR_STOPWORDS,
  normalizeWord,
} from '../modules/bundle-storefront';
import {
  VariantPopupRenderer,
  getDeviceDialogKind,
  resolveSeedVariantId,
  esc,
  priceToCents,
  shopifyImageSized,
  formatMoney,
  type ProductDetail,
  type ProductDetailVariant,
  type DeviceDialogKind,
  type VariantPopupLabels,
} from '../modules/bundle-variant-popup';

const ICON_CARET = `<svg aria-hidden="true" focusable="false" role="presentation" class="icon icon-caret" viewBox="0 0 10 6"><path fill-rule="evenodd" clip-rule="evenodd" d="M9.354.646a.5.5 0 00-.708 0L5 4.293 1.354.646a.5.5 0 00-.708.708l4 4a.5.5 0 00.708 0l4-4a.5.5 0 000-.708z" fill="currentColor"/></svg>`;
const ICON_CHECKMARK = `<svg class="icon icon-checkmark" width="11" height="7" viewBox="0 0 11 7" fill="none"><path d="M1.5 3.5L2.83333 4.75L4.16667 6L9.5 1" stroke="currentColor" stroke-width="1.75" stroke-linecap="round" stroke-linejoin="round"/></svg>`;
const ICON_CLOSE_SMALL = `<svg aria-hidden="true" focusable="false" role="presentation" width="12" height="13" class="icon icon-close-small" viewBox="0 0 12 13" fill="none"><path d="M8.48627 9.32917L2.82849 3.67098" stroke="currentcolor" stroke-linecap="round" stroke-linejoin="round"/><path d="M2.88539 9.38504L8.42932 3.61524" stroke="currentcolor" stroke-linecap="round" stroke-linejoin="round"/></svg>`;

// Add filter IDs (substring match) or labels (exact, lowercase) to hide them from the bundle filter panel.
const FILTER_EXCLUDED_IDS = ['product_type', 'option.size', '.plain', 'prefiltered'];
const FILTER_EXCLUDED_LABELS = ['prefiltered collection', 'collection préfiltrée'];

const DEFAULT_DEVICE_LABEL = 'iPhone 17'; // current most-common device — used as the device
                                           // filter's default when no favPhone cookie applies

// Each type's dedicated collection carries its own merchandised (manual) product order —
// browsing that type in the bundle tool should match what shoppers see on the collection
// page (e.g. Impact Cases), not a best-selling ranking pulled from the catch-all "all"
// collection. Types absent here (accessories subtypes, Corpo Phone Case) have no single
// matching origin collection and keep falling back to "all" / BEST_SELLING.
const PRODUCT_TYPE_ORIGIN_COLLECTION: Record<string, string> = {
  'Phone Case': 'impact-cases',
  'Clear Case': 'clear-case',
  'Folio Case': 'folio-case',
  'Airpod Cases': 'airpod-cases',
  'Skin': 'skins',
  'iPad Folio Case': 'ipad-folio-case',
  'iPad Clear Case': 'ipad-clear-cases',
  'ChargePods': 'chargepods',
  'Kindle Case': 'e-reader-cases',
};

function buildBundleGQL(langCode: string, deviceOptionName?: string, deviceValue?: string, collectionHandle = 'all'): string {
  const variantAddon = deviceOptionName && deviceValue
    ? `selectedOrFirstAvailableVariant(selectedOptions: [{ name:"${deviceOptionName}", value:"${deviceValue}"}]) {
          id
          image { url altText }
        }`
    : '';
  const sortKey = collectionHandle === 'all' ? 'BEST_SELLING' : 'COLLECTION_DEFAULT';
  return `
    query BundleProducts($cursor: String, $filters: [ProductFilter!]!) @inContext(language: ${langCode}) {
      collection(handle: "${collectionHandle}") {
        products(first: 24, after: $cursor, filters: $filters, sortKey: ${sortKey}) {
          pageInfo { hasNextPage endCursor }
          filters { id label type values { id label count input } }
          nodes {
            id title handle productType
            featuredImage { url altText }
            ${variantAddon}
            variants(first: 5) {
              nodes {
                id title availableForSale
                price { amount }
                compareAtPrice { amount }
              }
            }
          }
        }
      }
    }
  `;
}

// Unlike collection(handle:"all").products(filters:...), the top-level products() field
// takes a free-text search query and isn't scoped to a single product_type — this is what
// lets the search bar match products across every type instead of just the active one.
function buildBundleSearchGQL(langCode: string): string {
  return `
    query BundleSearch($cursor: String, $query: String!) @inContext(language: ${langCode}) {
      products(first: 24, after: $cursor, query: $query, sortKey: RELEVANCE) {
        pageInfo { hasNextPage endCursor }
        nodes {
          id title handle productType
          featuredImage { url altText }
          variants(first: 5) {
            nodes {
              id title availableForSale
              price { amount }
              compareAtPrice { amount }
            }
          }
        }
      }
    }
  `;
}

// ─── Config types ─────────────────────────────────────────────────────────────

interface BundleTier {
  minQty: number;
  discountPercent: number;
  addSlotLabel: string;
}

interface BundleConfig {
  collectionHandle: string;
  maxProducts: number;
  tiers: BundleTier[];
  lang: string;
  loaderUrl?: string;
  isVip: boolean;
  labels: Record<string, string>;
}

// ─── Storefront API types ─────────────────────────────────────────────────────

interface StorefrontFilterValue {
  id: string;
  label: string;
  count: number;
  input: string;
}

interface StorefrontFilter {
  id: string;
  label: string;
  type: string;
  values: StorefrontFilterValue[];
}

interface StorefrontVariantNode {
  id: string;
  title: string;
  availableForSale: boolean;
  price: { amount: string };
  compareAtPrice: { amount: string } | null;
}

interface StorefrontProductNode {
  id: string;
  title: string;
  handle: string;
  productType: string;
  featuredImage: { url: string; altText: string | null } | null;
  selectedOrFirstAvailableVariant?: { id: string; image: { url: string; altText: string | null } | null } | null;
  variants: { nodes: StorefrontVariantNode[] };
}

// ─── Shopify Ajax types (collections endpoint) ────────────────────────────────

interface ShopifyAjaxVariant {
  id: number;
  title: string;
  price: string;
  compare_at_price: string | null;
  available: boolean;
}

interface ShopifyAjaxProduct {
  id: number;
  title: string;
  handle: string;
  product_type: string;
  variants: ShopifyAjaxVariant[];
  featured_image: { src: string } | string | null;
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

function readCookie(name: string): string {
  return decodeURIComponent(
    document.cookie.split('; ').find((c) => c.startsWith(name + '='))?.split('=')[1] ?? ''
  );
}

function showToast(msg: string) {
  const el = document.createElement('div');
  el.className = 'bundle-toast';
  el.textContent = msg;
  document.body.appendChild(el);
  requestAnimationFrame(() => el.classList.add('is-visible'));
  setTimeout(() => el.remove(), 2800);
}

// ─── URL rehydration ──────────────────────────────────────────────────────────

// The URL only carries {handle, variantId} per product (bundle-state.ts's URLProductRef) —
// everything else is refetched here so a resumed draft/share/favorite link never shows a
// stale price, title, or image. A ref that no longer resolves (deleted/unpublished product,
// removed variant) is dropped silently — its slot just comes back empty.
async function rehydrateProducts(refs: URLProductRef[]): Promise<SelectedProduct[]> {
  const results = await Promise.allSettled(
    refs.map(async (ref): Promise<SelectedProduct> => {
      const res = await fetch(`/products/${ref.handle}.json`);
      if (!res.ok) throw new Error(`Product fetch failed: ${ref.handle}`);
      const { product } = (await res.json()) as { product: ProductDetail };
      const variant = product.variants.find((v) => String(v.id) === ref.variantId);
      if (!variant) throw new Error(`Variant not found: ${ref.variantId} on ${ref.handle}`);

      const imageSrc = variant.featured_image?.src
        ? shopifyImageSized(variant.featured_image.src, '400x')
        : shopifyImageSized(product.images[0]?.src ?? '', '400x');

      return {
        variantId:      String(variant.id),
        productId:      String(product.id),
        handle:         product.handle,
        productType:    product.product_type,
        title:          product.title,
        price:          priceToCents(variant.price),
        compareAtPrice: variant.compare_at_price ? priceToCents(variant.compare_at_price) : null,
        image:          imageSrc,
        variantTitle:   variant.title,
      };
    })
  );

  return results
    .filter((r): r is PromiseFulfilledResult<SelectedProduct> => r.status === 'fulfilled')
    .map((r) => r.value);
}

function toAjaxProduct(node: StorefrontProductNode): ShopifyAjaxProduct {
  return {
    id: gidToNumericId(node.id),
    title: node.title,
    handle: node.handle,
    product_type: node.productType,
    variants: node.variants.nodes.map((v) => ({
      id: gidToNumericId(v.id),
      title: v.title,
      price: v.price.amount,
      compare_at_price: v.compareAtPrice?.amount ?? null,
      available: v.availableForSale,
    })),
    featured_image: (() => {
      const src = node.selectedOrFirstAvailableVariant?.image?.url ?? node.featuredImage?.url ?? null;
      return src ? { src } : null;
    })(),
  };
}

// ─── Constants ────────────────────────────────────────────────────────────────

const PAGE_SIZE = 24;

// ─── Bundle Popup ─────────────────────────────────────────────────────────────

interface PopupOpenOpts {
  handle: string;
  title: string;
  defaultVariantId: string;
  discountPct: number;
  isFull: boolean;
  onAdd: (product: SelectedProduct) => void;
  lang: string;
  labels: Record<string, string>;
  deviceOptionName?: string;
  deviceValue?: string;
}

class BundlePopup {
  private dialog: HTMLDialogElement;
  private titleEl: HTMLElement;
  private closeBtn: HTMLButtonElement;
  private ctaBtn: HTMLButtonElement;
  private loadingEl: HTMLElement;
  private renderer: VariantPopupRenderer;

  private opts!: PopupOpenOpts;
  private prevFocus: HTMLElement | null = null;

  private deviceDialogs: Partial<Record<DeviceDialogKind, { dialog: Dialog; el: HTMLDialogElement }>> = {};
  private caseTypeDialog: { dialog: Dialog; el: HTMLDialogElement } | null = null;

  constructor() {
    this.dialog = document.getElementById('bundle-product-popup') as HTMLDialogElement;
    this.titleEl = this.dialog.querySelector('.bundle-popup__title') as HTMLElement;
    this.closeBtn = this.dialog.querySelector('[data-dialog-toggle]') as HTMLButtonElement;
    this.ctaBtn = this.dialog.querySelector('.bundle-popup__cta') as HTMLButtonElement;
    this.loadingEl = this.dialog.querySelector('.bundle-popup__loading') as HTMLElement;

    this.renderer = new VariantPopupRenderer(this.dialog, {}, 'en', {
      onOptionChange: () => this.updateCTA(),
      onOpenDeviceHelp: (kind) => this.openDeviceHelp(kind),
      onOpenCaseTypeHelp: () => this.caseTypeDialog?.dialog.open(),
      // This popup's host page (sections/main-bundle-tool.liquid) always statically
      // renders the case-type-help dialog markup, so this is unconditionally true here.
      hasCaseTypeHelp: true,
    });

    this.closeBtn.addEventListener('click', () => this.close());

    this.dialog.addEventListener('click', (e) => {
      if (e.target === this.dialog) this.close();
    });

    this.dialog.addEventListener('cancel', (e) => {
      e.preventDefault();
      this.close();
    });

    this.ctaBtn.addEventListener('click', () => this.handleCTA());

    this.initDeviceDialogs();
  }

  private initDeviceDialogs() {
    const containerIds: Record<DeviceDialogKind, string> = {
      phone: 'bundle-device-help-phone',
      clearCase: 'bundle-device-help-clear-case',
      kindle: 'bundle-device-help-kindle',
      skin: 'bundle-device-help-skin',
    };

    (Object.keys(containerIds) as DeviceDialogKind[]).forEach((kind) => {
      const el = document.querySelector<HTMLDialogElement>(`#${containerIds[kind]} dialog`);
      if (!el) return;
      const closeBtns = el.querySelectorAll<HTMLElement>('[data-dialog-toggle]');
      this.deviceDialogs[kind] = { dialog: new Dialog(el, [], closeBtns), el };
      this.relockScrollOnNestedClose(el);
    });

    const caseTypeEl = document.querySelector<HTMLDialogElement>('#bundle-device-help-case-type dialog');
    if (caseTypeEl) {
      const closeBtns = caseTypeEl.querySelectorAll<HTMLElement>('[data-dialog-toggle]');
      this.caseTypeDialog = { dialog: new Dialog(caseTypeEl, [], closeBtns), el: caseTypeEl };
      this.relockScrollOnNestedClose(caseTypeEl);
    }
  }

  // A nested device/case-type Dialog's own close() unconditionally clears
  // --no-scroll — correct when it's the only thing open, wrong here since
  // it's nested inside this.dialog, which stays open underneath it. Re-lock
  // right after its close() finishes removing the class.
  private relockScrollOnNestedClose(nestedDialogEl: HTMLDialogElement) {
    nestedDialogEl.addEventListener('close', () => {
      if (this.dialog.open) {
        setTimeout(() => document.body.classList.add('--no-scroll'), 0);
      }
    });
  }

  private openDeviceHelp(kind: DeviceDialogKind) {
    const entry = this.deviceDialogs[kind];
    if (!entry) return;

    if (kind === 'phone') {
      const isFolio = this.renderer.getProduct()?.product_type === 'Folio Case';
      entry.el.querySelectorAll<HTMLElement>('[data-target="pixel"]').forEach((btn) => { btn.hidden = isFolio; });

      const tabButtons = entry.el.querySelectorAll<HTMLButtonElement>('[data-target]');
      const groups = entry.el.querySelectorAll<HTMLElement>('[data-dialog-group]');
      tabButtons.forEach((btn) => btn.classList.toggle('active', btn.dataset.target === 'iphone'));
      groups.forEach((group) => group.classList.toggle('active', group.dataset.dialogGroup === 'iphone'));
    } else if (kind === 'kindle' || kind === 'skin') {
      this.renderer.populateModelList(entry.el, () => {
        this.deviceDialogs.kindle?.dialog.close();
        this.deviceDialogs.skin?.dialog.close();
      });
    }

    entry.dialog.open();
  }

  async open(opts: PopupOpenOpts) {
    this.opts = opts;
    this.prevFocus = document.activeElement as HTMLElement;

    this.renderer.labels = opts.labels as VariantPopupLabels;
    this.renderer.lang = opts.lang;

    this.titleEl.textContent = opts.title;
    this.loadingEl.hidden = false;
    this.ctaBtn.hidden = true;

    this.dialog.classList.remove('is-closing');
    this.dialog.showModal();
    document.body.classList.add('--no-scroll');

    try {
      const [res, availability] = await Promise.all([
        fetch(`/products/${opts.handle}.json`),
        fetchVariantAvailability(opts.handle).catch(() => new Map<number, boolean>()),
      ]);
      if (!res.ok) throw new Error();
      const { product } = (await res.json()) as { product: ProductDetail };
      product.variants.forEach((v) => {
        const known = availability.get(v.id);
        if (known !== undefined) v.available = known;
      });

      const seedVariantId = resolveSeedVariantId(product, opts.defaultVariantId, opts.deviceOptionName, opts.deviceValue);
      this.renderer.render(product, seedVariantId);
      this.updateCTA();
    } catch {
      // fail silently — keep popup open with loading hidden
    } finally {
      this.loadingEl.hidden = true;
      this.ctaBtn.hidden = false;
    }
  }

  close() {
    this.dialog.classList.add('is-closing');
    this.dialog.addEventListener('animationend', () => {
      this.dialog.classList.remove('is-closing');
      this.dialog.close();
      document.body.classList.remove('--no-scroll');
      this.prevFocus?.focus();
    }, { once: true });
  }

  updateDiscountPct(pct: number) {
    if (!this.opts) return;
    this.opts = { ...this.opts, discountPct: pct };
    if (this.dialog.open) this.updateCTA();
  }

  private updateCTA() {
    const variant = this.renderer.getSelectedVariant();
    const { lang, isFull } = this.opts;

    if (!variant?.available) {
      this.ctaBtn.disabled = true;
      this.ctaBtn.textContent = lang === 'fr' ? 'Épuisé' : 'Sold out';
      return;
    }

    if (isFull) {
      this.ctaBtn.disabled = true;
      this.ctaBtn.textContent = lang === 'fr' ? 'Bundle complet' : 'Bundle full';
      return;
    }

    // Price temporarily removed from this button — see project_bundles memory
    // 2026-07-27 (card price never fully matched the popup's device-specific
    // price; showing a price here that can be wrong is worse than showing none).
    this.ctaBtn.disabled = false;
    this.ctaBtn.textContent = lang === 'fr' ? 'Ajouter au bundle' : 'Add to Bundle';
  }

  private handleCTA() {
    const variant = this.renderer.getSelectedVariant();
    const product = this.renderer.getProduct();
    if (!variant || !product) return;

    const imageSrc = variant.featured_image?.src
      ? shopifyImageSized(variant.featured_image.src, '400x')
      : shopifyImageSized(product.images[0]?.src ?? '', '400x');

    const selected: SelectedProduct = {
      variantId:      String(variant.id),
      productId:      String(product.id),
      handle:         product.handle,
      productType:    product.product_type,
      title:          product.title,
      price:          priceToCents(variant.price),
      compareAtPrice: variant.compare_at_price ? priceToCents(variant.compare_at_price) : null,
      image:          imageSrc,
      variantTitle:   variant.title,
    };

    this.opts.onAdd(selected);
    this.close();
  }
}

// ─── Main page controller ─────────────────────────────────────────────────────

class BundlePage {
  private config!: BundleConfig;
  private selected: SelectedProduct[] = [];
  private activeTypeHandle = '';
  private searchQuery = '';
  private searchDebounceTimer: number | null = null;
  private panelOpen = false;
  private searchVisible = false;
  private popup!: BundlePopup;

  // Pagination state
  private cursor: string | null = null;
  private isLoading = false;
  private hasMore = true;
  private loadedCollection = '';
  // Products already rendered by the pinned-match query for the current type-browse
  // session (see fetchPinnedMatches) — the normal paginated results are filtered
  // against this so a matching product never appears twice.
  private pinnedProductIds: Set<string> = new Set();
  // Bumped on every reset (type switch or new search). Type-browse and search fetches
  // share isLoading/hasMore/cursor, so a slow request from a mode the user has since left
  // could otherwise land after a newer one and either get silently dropped by the isLoading
  // guard or clobber fresher results — the token lets a stale response detect it's stale.
  private loadToken = 0;
  // Snapshot of the type-browse grid taken right before a search replaces it, so closing
  // the search can restore the prior list without refetching it from the Storefront API.
  private browseCache: { key: string; html: string; cursor: string | null; hasMore: boolean } | null = null;

  // Filter state
  private activeDeviceFilter = '';
  private activeFilters: string[] = [];
  private filterMetadata: StorefrontFilter[] = [];

  // DOM refs
  private section!: HTMLElement;
  private typeSelect!: HTMLSelectElement;
  // Choices.js instance for typeSelect (undefined if the lib failed to load —
  // every typeSelect write goes through setTypeSelectValue()/updateTypeLockState()
  // below, which fall back to plain native-select DOM ops when this is unset).
  private typeSelectChoices: any;
  // Snapshot of the Liquid-rendered <option>s (value/label), taken before Choices
  // touches the DOM — updateTypeLockState() rebuilds from this on every render
  // instead of reading this.typeSelect.options, since Choices.setChoices() owns
  // that list once initialized.
  private typeOptionsBase: { value: string; label: string }[] = [];
  private searchInput!: HTMLInputElement | null;
  private searchWrap!: HTMLElement | null;
  private searchBtn!: HTMLButtonElement | null;
  private grid!: HTMLUListElement;
  private gridWrapper!: HTMLElement | null;
  private loadingEl!: HTMLElement | null;
  private categoryGridEl!: HTMLElement | null;
  // Suppresses updateTypeLockState()'s "used type -> show category grid" check for the
  // single initial render triggered from init(). syncTypeSelectToSelection() sets
  // activeTypeHandle to the first restored product's type, which is always already in
  // usedTypes by construction — without this flag that first render would always show
  // the category grid instead of the product grid for a reopened saved/shared bundle.
  private isRestoringSelection = false;
  private filtersDialog: HTMLDialogElement | null = null;
  private filtersPanel: HTMLElement | null = null;
  private filterBtn: HTMLButtonElement | null = null;
  private bar!: HTMLElement;
  private barBackdrop!: HTMLElement;
  private barExpandBtn!: HTMLButtonElement;
  private barSlotRow!: HTMLElement;
  private barStripTitleText!: HTMLElement;
  private breadcrumbCurrent: HTMLElement | null = null;
  private panelTitle: HTMLElement | null = null;
  private barStripSubtitle!: HTMLElement;
  private barPanel!: HTMLElement;
  private barPanelItems!: HTMLUListElement;
  private barOriginalPrice!: HTMLElement;
  private barDiscountedPrice!: HTMLElement;
  private barDiscountBadge!: HTMLElement;
  private totalRow!: HTMLElement;
  private barMentions!: NodeListOf<HTMLElement>;
  private saveBtns: HTMLButtonElement[] = [];
  private cartBtns: HTMLButtonElement[] = [];
  private nameDialogEl: HTMLDialogElement | null = null;
  private nameDialog!: NameDialog;
  private replaceDialog: ReplaceConfirmDialog | null = null;
  private savedBundleId: string | null = null;
  // Set when this session was opened from the cart drawer's bundle summary
  // (cart-bundle-group.ts's "click the name to edit" link) via a `cartGroupId`
  // URL param. A cart line's variant+properties are its identity, so there's
  // no in-place "update" — handleAddToCart() removes this group's existing
  // lines before adding the edited selection as a fresh one.
  private editingCartGroupId: string | null = null;
  // Captured in bindDOM(), before init() may overwrite them with a reopened saved
  // bundle's name — lets resetBuilder() restore the page's own default copy.
  private defaultBundleTitle = '';
  private defaultBreadcrumbTitle = '';
  // typeName carried in from the URL — set for signature/pre-configured bundles
  // (bundle-list-signature.liquid, bundle-list-cart.liquid) or when reopening an
  // already-saved bundle. Falls back to the generic "Custom bundle" label on save
  // when empty, so the shopper's own original signature name survives edits.
  private incomingTypeName: string = '';

  // Load more (manual pagination)
  private loadMoreBtn: HTMLButtonElement | null = null;
  private emptyStateEl: HTMLElement | null = null;

  async init() {
    const configEl = document.getElementById('bundle-tool-config');
    if (!configEl?.textContent) return;

    try {
      this.config = JSON.parse(configEl.textContent) as BundleConfig;
    } catch {
      return;
    }

    this.section = document.getElementById('bundle-tool-section') as HTMLElement;
    this.bar     = document.getElementById('bundle-bar') as HTMLElement;
    this.barBackdrop = document.getElementById('bundle-bar-backdrop') as HTMLElement;
    if (!this.section || !this.bar) return;

    const urlParams = new URLSearchParams(window.location.search);
    this.savedBundleId = urlParams.get('bundleId');
    this.editingCartGroupId = urlParams.get('cartGroupId');

    this.bindDOM();

    if (this.savedBundleId) {
      const existingName = readSavedBundles().find((b) => b.id === this.savedBundleId)?.name;
      if (existingName) {
        this.barStripTitleText.textContent = existingName;
        if (this.breadcrumbCurrent) this.breadcrumbCurrent.textContent = existingName;
        if (this.panelTitle) this.panelTitle.textContent = existingName;
      }
    }

    await this.loadFromURL();
    this.syncTypeSelectToSelection();
    this.bindEvents();
    this.popup = new BundlePopup();
    // Only the very first render (right after restoring from the URL) needs the
    // lockout check suppressed — every render triggered afterward by user actions
    // (addProduct, selectType, removeProduct, etc.) must use the normal behavior.
    this.isRestoringSelection = this.selected.length > 0;
    this.render();
    this.isRestoringSelection = false;

    if (this.activeTypeHandle) {
      this.loadProductsForType(this.activeTypeHandle, true);
    }
  }

  private bindDOM() {
    this.typeSelect   = this.section.querySelector('.bundle-tool__type-select') as HTMLSelectElement;
    this.typeOptionsBase = Array.from(this.typeSelect.options).map((opt) => ({
      value: opt.value,
      label: opt.textContent ?? '',
    }));
    if (typeof (window as any).Choices !== 'undefined') {
      // Native <select>.showPicker() has no Safari support at all (desktop or
      // iOS, confirmed live — not just older versions), and there's no scripted
      // way to open a native select's dropdown without it. Choices.js renders
      // its own dropdown UI on top of the (now hidden) native select, so
      // openTypeSelect() can open it via choicesInstance.showDropdown() on every
      // browser — see openTypeSelect() below.
      this.typeSelectChoices = new (window as any).Choices(this.typeSelect, {
        searchEnabled: this.typeSelect.options.length > 10,
        itemSelectText: '',
        shouldSort: false,
      });
    }
    this.searchInput  = this.section.querySelector<HTMLInputElement>('.bundle-tool__search');
    this.searchWrap   = this.section.querySelector<HTMLElement>('.bundle-tool__search-wrap');
    this.searchBtn    = this.section.querySelector<HTMLButtonElement>('.bundle-tool__search-btn');
    this.grid         = this.section.querySelector('.bundle-tool__grid') as HTMLUListElement;
    this.gridWrapper  = this.section.querySelector<HTMLElement>('.bundle-tool__grid-wrapper');
    this.loadingEl    = this.section.querySelector<HTMLElement>('.bundle-tool__loading');
    this.loadMoreBtn  = this.section.querySelector<HTMLButtonElement>('[data-load-more]');
    this.emptyStateEl = this.section.querySelector<HTMLElement>('[data-empty-state]');
    // Selects the section wrapper (heading/text + grid), not the grid itself,
    // so the optional intro copy above the cards shares the same hidden state.
    this.categoryGridEl = this.section.querySelector<HTMLElement>('.bundle-tool__category-section');
    this.breadcrumbCurrent = this.section.querySelector<HTMLElement>('#bundle-breadcrumb-current');
    this.panelTitle = this.bar.querySelector<HTMLElement>('#bundle-panel-title');

    this.barExpandBtn     = this.bar.querySelector('.bundle-bar__expand-btn') as HTMLButtonElement;
    this.barSlotRow       = this.bar.querySelector('.bundle-bar__slot-row') as HTMLElement;
    this.barStripTitleText = this.bar.querySelector('.bundle-bar__strip-title-text') as HTMLElement;
    this.defaultBundleTitle = this.barStripTitleText.textContent ?? '';
    this.defaultBreadcrumbTitle = this.breadcrumbCurrent?.textContent ?? '';
    this.barStripSubtitle = this.bar.querySelector('.bundle-bar__strip-subtitle') as HTMLElement;
    this.barStripSubtitle.textContent =
      (this.config.labels.pickProducts ?? '').replace('{n}', String(this.config.maxProducts));
    this.barPanel         = this.bar.querySelector('.bundle-bar__panel') as HTMLElement;
    this.barPanelItems    = this.bar.querySelector('.bundle-bar__items') as HTMLUListElement;
    this.barOriginalPrice    = this.bar.querySelector('.bundle-bar__original-price') as HTMLElement;
    this.barDiscountedPrice  = this.bar.querySelector('.bundle-bar__discounted-price') as HTMLElement;
    this.totalRow    = this.bar.querySelector('.bundle-bar__summary') as HTMLElement;
    this.barDiscountBadge    = this.bar.querySelector('.bundle-bar__discount-badge') as HTMLElement;
    this.barMentions         = this.bar.querySelectorAll('.bundle-bar__mention[data-lien-template]');

    this.saveBtns = Array.from(this.bar.querySelectorAll<HTMLButtonElement>('.bundle-bar__save'));
    this.cartBtns = Array.from(this.bar.querySelectorAll<HTMLButtonElement>(
      '.bundle-bar__add-to-cart'
    ));

    this.filtersDialog = document.getElementById('bundle-filters-dialog') as HTMLDialogElement | null;
    this.filtersPanel  = this.filtersDialog?.querySelector<HTMLElement>('[data-dialog-content]') ?? null;
    this.filterBtn     = this.section.querySelector<HTMLButtonElement>('.bundle-tool__filter-btn');

    const savedDialogEl = document.getElementById('bundle-saved-dialog') as HTMLDialogElement | null;
    if (savedDialogEl) {
      const openBtns = document.querySelectorAll<HTMLElement>('[data-open-saved-bundles]');
      const closeBtns = savedDialogEl.querySelectorAll<HTMLElement>('[data-dialog-toggle]');
      new Dialog(savedDialogEl, openBtns, closeBtns);
    }

    this.nameDialogEl = document.getElementById('bundle-name-dialog') as HTMLDialogElement | null;
    if (this.nameDialogEl) this.nameDialog = new NameDialog(this.nameDialogEl);

    const replaceDialogEl = document.getElementById('bundle-replace-confirm-dialog') as HTMLDialogElement | null;
    if (replaceDialogEl) {
      this.replaceDialog = new ReplaceConfirmDialog(replaceDialogEl);
      window.kaseMeBundleReplaceGuard = {
        hasUnsaved: () => this.selected.length > 0,
        confirm: () => this.replaceDialog!.confirm(),
      };
    }
  }

  private async loadFromURL() {
    const state = readStateFromURL();
    if (state?.products?.length) {
      this.selected = await rehydrateProducts(state.products);
    }
    this.incomingTypeName = state?.typeName || '';
  }

  // Reopening a saved/shared bundle (state.products carried in the URL) should
  // show that bundle's product grid, not the empty-state category-card grid —
  // derive the type <select>'s initial value from the first selected product's
  // type when one exists. A brand-new visit with no URL state leaves the blank
  // placeholder option selected, which is what triggers the category grid (see
  // updateTypeLockState).
  // Looks up typeOptionsBase, not this.typeSelect.options: once Choices.js is
  // initialized the native <select> only keeps the currently selected <option>,
  // so the lookup always missed and a restored bundle landed on an empty grid
  // with the category picker hidden.
  private syncTypeSelectToSelection() {
    if (this.selected.length === 0) return;
    const productType = this.selected[0].productType;
    if (this.typeOptionsBase.some((opt) => opt.value === productType)) {
      this.setTypeSelectValue(productType);
      this.activeTypeHandle = productType;
    }
  }

  // Choices.js doesn't observe a plain `select.value = x` assignment (it keeps
  // its own internal store and only reacts to real user interaction or its own
  // API), so every programmatic value change has to go through this instead of
  // touching this.typeSelect.value directly. setChoiceByValue() doesn't fire a
  // native 'change' event, matching the native-select assignment it replaces.
  private setTypeSelectValue(value: string) {
    if (this.typeSelectChoices) this.typeSelectChoices.setChoiceByValue(value);
    else this.typeSelect.value = value;
  }

  private selectType(handle: string, source: 'dropdown' | 'category_grid' = 'dropdown') {
    this.setTypeSelectValue(handle);
    this.activeTypeHandle = handle;
    this.activeDeviceFilter = '';
    this.activeFilters = [];
    this.updateTypeLockState();
    // Picking the blank placeholder option means "back to the category grid" —
    // there's no type to fetch products for, so the rest (which re-renders the
    // product grid and tracks an event) only applies to a real type handle.
    if (!handle) return;
    this.loadProductsForType(handle, true);
    this.gridWrapper?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    trackEvent('bundle_type_select', { product_type: handle, source });
  }

  private bindEvents() {
    this.typeSelect.addEventListener('change', () => this.selectType(this.typeSelect.value));
    this.loadMoreBtn?.addEventListener('click', this.handleLoadMoreClick);

    this.categoryGridEl?.addEventListener('click', (e) => {
      const btn = (e.target as HTMLElement).closest<HTMLButtonElement>('[data-select-type]');
      if (btn?.dataset.selectType) this.selectType(btn.dataset.selectType, 'category_grid');
    });

    this.filterBtn?.addEventListener('click', () => {
      if (this.filtersDialog) {
        this.filtersDialog.classList.remove('is-closing');
        this.filtersDialog.showModal();
        document.body.classList.add('--no-scroll');
      }
    });

    this.filtersDialog?.querySelector('[data-dialog-toggle]')?.addEventListener('click', () => {
      this.closeFiltersDialog();
    });

    this.filtersDialog?.addEventListener('click', (e) => {
      if (e.target === this.filtersDialog) this.closeFiltersDialog();
    });

    this.filtersDialog?.addEventListener('cancel', (e) => {
      e.preventDefault();
      this.closeFiltersDialog();
    });


    this.searchBtn?.addEventListener('click', () => {
      this.searchVisible = !this.searchVisible;
      this.searchBtn!.setAttribute('aria-expanded', String(this.searchVisible));
      this.section
        .querySelector('.bundle-tool__toolbar')
        ?.classList.toggle('--search-open', this.searchVisible);

      if (this.searchWrap) {
        if (this.searchVisible) {
          this.searchWrap.hidden = false;
          void this.searchWrap.offsetWidth; // force reflow so the enter transition plays
          this.searchWrap.classList.add('is-open');
          this.searchInput?.focus();
        } else {
          this.searchWrap.classList.remove('is-open');
          this.searchWrap.hidden = true;
          if (this.searchDebounceTimer) {
            window.clearTimeout(this.searchDebounceTimer);
            this.searchDebounceTimer = null;
          }
          if (this.searchQuery) {
            this.searchQuery = '';
            if (this.searchInput) this.searchInput.value = '';
            this.restoreBrowseState();
          }
        }
      }
    });

    this.searchInput?.addEventListener('input', () => {
      this.searchQuery = this.searchInput!.value.toLowerCase().trim();
      if (this.searchDebounceTimer) window.clearTimeout(this.searchDebounceTimer);
      this.searchDebounceTimer = window.setTimeout(() => this.handleSearchChange(), 300);
    });

    this.grid.addEventListener('click', (e) => this.handleGridClick(e));

    this.barExpandBtn.addEventListener('click', () => {
      if (this.selected.length === 0) return;
      if (this.panelOpen) this.closePanel(); else this.openPanel();
    });
    this.barBackdrop.addEventListener('click', () => this.closePanel());
    this.saveBtns.forEach((btn) => btn.addEventListener('click', () => this.handleSave()));
    this.cartBtns.forEach((btn) => btn.addEventListener('click', () => this.handleAddToCart()));
  }

  // ─── Filter UI ────────────────────────────────────────────────────────────────

  private renderFilterUI() {
    if (!this.filtersPanel || !this.filterMetadata.length) return;

    const isFr = this.config.lang === 'fr';
    const resetLabel = isFr ? 'Réinitialiser' : 'Reset';

    // Default the device filter on first render: favPhone cookie if it matches one of this
    // type's available devices, else DEFAULT_DEVICE_LABEL if the type offers it (e.g. Kindle
    // Case has no iPhone option, so it's left unselected in that case). Only an explicit user
    // selection persists favPhone (bindFilterEvents) — this fallback never writes the cookie.
    //
    // readCookie() returns '' (not undefined) when the cookie is absent. An earlier version of
    // this guarded the lookup with `favPhone && deviceMeta.values.find(...)`, which — when
    // favPhone is '' — short-circuits to '' itself (not undefined/null), so the `?? fallback`
    // that followed never ran: '' is not nullish, so `?? ` passes it straight through. That
    // silently disabled the fallback for every visitor with no cookie, i.e. the common case.
    // find() naturally returns undefined for a '' search (no device is labeled ''), so the
    // `favPhone &&` guard was both unnecessary and the bug — removed below.
    if (!this.activeDeviceFilter) {
      const deviceMeta = this.filterMetadata.find(
        (f) => f.id.includes('option.device') || ['device', 'appareil'].includes(f.label.toLowerCase())
      );
      if (deviceMeta) {
        const favPhone = readCookie('favPhone');
        const match = deviceMeta.values.find((v) => v.label === favPhone)
          ?? deviceMeta.values.find((v) => v.label === DEFAULT_DEVICE_LABEL);
        if (match) this.activeDeviceFilter = match.input;
      }
    }

    let sectionsHtml = '';

    this.filterMetadata.forEach((filter, idx) => {
      const labelLower = filter.label.toLowerCase();
      if (FILTER_EXCLUDED_IDS.some((e) => filter.id.includes(e))) return;
      if (FILTER_EXCLUDED_LABELS.includes(labelLower)) return;
      const visible = filter.values.filter((v) => v.count > 0);
      if (!visible.length) return;

      const isDevice = filter.id.includes('option.device')
        || labelLower === 'device'
        || labelLower === 'appareil';

      if (isDevice) {
        sectionsHtml += this.buildDeviceFilterHtml(filter, visible, idx, isFr, this.activeDeviceFilter);
      } else if (filter.label === 'Collections') {
        sectionsHtml += this.buildCollectionsFilterHtml(filter, visible, idx, this.activeFilters);
      } else {
        sectionsHtml += this.buildCheckboxFilterHtml(filter, visible, idx, this.activeFilters);
      }
    });

    if (!sectionsHtml) {
      this.filtersPanel.innerHTML = '';
      return;
    }

    this.filtersPanel.innerHTML = `
      <div class="filters-container bundle-tool__filters-inner">
        <div class="filters">
          <div class="filters__form">
            ${sectionsHtml}
            <button type="button" class="button button--text filters__reset bundle-tool__filter-reset" hidden>
              ${esc(resetLabel)} ${ICON_CLOSE_SMALL}
            </button>
          </div>
        </div>
      </div>`;

    this.bindFilterEvents();
    hydrateCollectionsFilterUI(this.filtersPanel).then(() => this.updateCollectionsFilterBadges());
  }

  // Collections filter badges show the collection's friendly title (hydrated async into
  // .collection-filter-title), not the raw handle in the checkbox's value/label — mirrors
  // the real collection page's updateFilterSummaryLabels, which reads the same rendered text.
  private updateCollectionsFilterBadges() {
    if (!this.filtersPanel) return;
    const sections = new Set<HTMLElement>();
    this.filtersPanel.querySelectorAll<HTMLElement>('[data-collection-filter-item]').forEach((item) => {
      const section = item.closest<HTMLElement>('.filter__disclosure');
      if (section) sections.add(section);
    });
    sections.forEach((section) => {
      const badge = section.querySelector<HTMLElement>('.filter__active');
      if (!badge) return;
      const titles = Array.from(section.querySelectorAll<HTMLInputElement>('.bundle-filter__checkbox:checked'))
        .map((cb) => cb.closest('[data-collection-filter-item]')?.querySelector('.collection-filter-title')?.textContent?.trim() || '')
        .filter(Boolean);
      badge.textContent = titles.join(', ');
    });
  }

  private buildDeviceFilterHtml(
    filter: StorefrontFilter,
    values: StorefrontFilterValue[],
    idx: number,
    isFr: boolean,
    activeDevice: string
  ): string {
    const allLabel = isFr ? 'Tous les appareils' : 'All devices';
    const activeLabel = activeDevice ? (values.find((v) => v.input === activeDevice)?.label ?? '') : '';

    const buildOption = (v: StorefrontFilterValue): string =>
      `<option value="${esc(v.input)}" data-label="${esc(v.label)}"${activeDevice === v.input ? ' selected' : ''}>${esc(v.label)}</option>`;

    // Highest number first (iPhone 17 before iPhone 7)
    const sortDescending = (list: StorefrontFilterValue[]): StorefrontFilterValue[] =>
      [...list].sort((a, b) => b.label.localeCompare(a.label, undefined, { numeric: true, sensitivity: 'base' }));

    // Within iPhone, models with no number right after "iPhone" (X/XS, XR, SE 2020 / 2022...) sort last
    const sortIPhone = (list: StorefrontFilterValue[]): StorefrontFilterValue[] => {
      const hasNumber = (label: string) => /^iPhone\s+\d/i.test(label);
      const numbered = list.filter((v) => hasNumber(v.label));
      const unnumbered = list.filter((v) => !hasNumber(v.label));
      return [
        ...sortDescending(numbered),
        ...unnumbered.sort((a, b) => a.label.localeCompare(b.label, undefined, { numeric: true, sensitivity: 'base' })),
      ];
    };

    // Group device values by brand, matching the classification used for the custom designer's device picker
    const brandGroups: { label: string; test: (label: string) => boolean; sort: (list: StorefrontFilterValue[]) => StorefrontFilterValue[] }[] = [
      { label: 'iPhone', test: (l) => l.includes('iPhone'), sort: sortIPhone },
      { label: 'Samsung', test: (l) => l.includes('Samsung') || l.includes('Galaxy'), sort: sortDescending },
      { label: 'Pixel', test: (l) => l.includes('Pixel') || l.includes('Google'), sort: sortDescending },
    ];

    const remaining = [...values];
    const groupedOpts = brandGroups.map(({ label, test, sort }) => {
      const matches = remaining.filter((v) => test(v.label));
      if (!matches.length) return '';
      matches.forEach((v) => remaining.splice(remaining.indexOf(v), 1));
      return `<optgroup label="${esc(label)}">${sort(matches).map(buildOption).join('')}</optgroup>`;
    }).join('');

    const otherLabel = isFr ? 'Autres' : 'Other';
    const otherOpts = remaining.length
      ? `<optgroup label="${esc(otherLabel)}">${sortDescending(remaining).map(buildOption).join('')}</optgroup>`
      : '';

    const opts = groupedOpts + otherOpts;

    return `
      <details class="disclosure-has-popup filter__disclosure js-filter" data-index="${idx}">
        <summary class="filter__summary --first-level">
          <span class="filter__label">${esc(filter.label)}</span>
          <span class="filter__active">${activeLabel}</span>
          ${ICON_CARET}
        </summary>
        <div class="filters__display">
          <div class="filters__list filters__list--divided list-unstyled">
            <div class="regular-filters">
              <select class="bundle-filter__device" data-filter-id="${esc(filter.id)}">
                <option value="">${esc(allLabel)}</option>
                ${opts}
              </select>
            </div>
          </div>
        </div>
      </details>`;
  }

  private buildCheckboxFilterHtml(
    filter: StorefrontFilter,
    values: StorefrontFilterValue[],
    idx: number,
    activeFilters: string[]
  ): string {
    const activeLabels: string[] = [];
    const items = values.map((v, i) => {
      const id = `BFilter-${idx}-${i}`;
      const checked = activeFilters.includes(v.input);
      if (checked) activeLabels.push(v.label);
      return `
        <li class="list-menu__item filters__item">
          <label for="${id}" class="filter-checkbox">
            <input
              type="checkbox"
              class="bundle-filter__checkbox"
              value="${esc(v.input)}"
              data-filter-id="${esc(filter.id)}"
              id="${id}"
              ${checked ? 'checked' : ''}
            >
            <div class="filter-checkbox__icon"></div>
            ${ICON_CHECKMARK}
            ${esc(v.label)}
          </label>
        </li>`;
    }).join('');

    const badgeText = activeLabels.join(', ');

    return `
      <details class="disclosure-has-popup filter__disclosure js-filter" data-index="${idx}">
        <summary class="filter__summary --first-level">
          <span class="filter__label">${esc(filter.label)}</span>
          <span class="filter__active">${badgeText}</span>
          ${ICON_CARET}
        </summary>
        <div class="filters__display">
          <div class="filters__list filters__list--divided list-unstyled">
            <ul class="regular-filters" role="list">${items}</ul>
          </div>
        </div>
      </details>`;
  }

  // "Collections" filter values are collection handles (same convention as the real collection
  // page's facet — see snippets/facets-product-list.liquid). Images aren't part of the filter
  // facet data, so this renders the same placeholder markup the real page does and lets
  // hydrateCollectionsFilterUI() fetch each collection's image by handle after render.
  private buildCollectionsFilterHtml(
    filter: StorefrontFilter,
    values: StorefrontFilterValue[],
    idx: number,
    activeFilters: string[]
  ): string {
    const activeLabels: string[] = [];
    const items = values.map((v, i) => {
      const id = `BFilter-${idx}-${i}`;
      const checked = activeFilters.includes(v.input);
      // v.label is the collection handle (see comment above) — a friendly title is fetched
      // async by hydrateCollectionsFilterUI, but a prior hydration pass this session may
      // have already cached it, so a re-render (e.g. after applying another filter) can
      // show it immediately instead of flashing the handle again.
      const cachedTitle = peekCollectionFilterCard(v.label)?.title || '';
      if (checked) activeLabels.push(cachedTitle || v.label);
      return `
        <li class="list-menu__item filters__item filters__item--image" data-collection-filter-item data-filter-collection-handle="${esc(v.label)}">
          <label for="${id}" class="filter-checkbox">
            <input
              type="checkbox"
              class="bundle-filter__checkbox"
              value="${esc(v.input)}"
              data-filter-id="${esc(filter.id)}"
              id="${id}"
              ${checked ? 'checked' : ''}
            >
            <span class="collection-filter-media" aria-hidden="true">
              <span class="collection-filter-skeleton"></span>
              <img class="collection-filter-image" width="150" height="150" loading="lazy" alt="" hidden>
            </span>
            <span class="collection-filter-title">${esc(cachedTitle)}</span>
          </label>
        </li>`;
    }).join('');

    const badgeText = activeLabels.join(', ');

    return `
      <details class="disclosure-has-popup filter__disclosure js-filter" data-index="${idx}">
        <summary class="filter__summary --first-level">
          <span class="filter__label">${esc(filter.label)}</span>
          <span class="filter__active">${badgeText}</span>
          ${ICON_CARET}
        </summary>
        <div class="filters__display">
          <div class="filters__list filters__list--divided list-unstyled">
            <ul class="regular-filters" role="list">${items}</ul>
          </div>
        </div>
      </details>`;
  }

  private bindFilterEvents() {
    if (!this.filtersPanel) return;

    // Device select
    const devSel = this.filtersPanel.querySelector<HTMLSelectElement>('.bundle-filter__device');
    if (devSel) {
      devSel.addEventListener('change', () => {
        this.activeDeviceFilter = devSel.value;
        this.updateFilterActive(devSel.closest<HTMLElement>('.filter__disclosure'), devSel.value ? 1 : 0);
        this.updateFilterReset();
        this.loadProductsForType(this.typeSelect.value, true, false);
        trackEvent('bundle_filter_apply', {
          filter_id: devSel.dataset.filterId ?? 'device',
          active: Boolean(devSel.value),
        });
        if (devSel.value) {
          // Choices.js rewrites the selected <option> to its own internal representation on every
          // pick (native user click or programmatic), which strips our custom `data-label`
          // attribute — it silently read as undefined here, so the cookie was never written on a
          // real user selection. `textContent` survives that rewrite (Choices needs it for its
          // own display), so read the label from there instead.
          const label = devSel.selectedOptions[0]?.textContent?.trim() ?? '';
          if (label) document.cookie = `favPhone=${encodeURIComponent(label)}; path=/; max-age=31536000`;
        }
      });
      if (typeof (window as any).Choices !== 'undefined') {
        // Choices.js defaults to shouldSort: true, which re-alphabetizes the list on render
        // and silently overrides the brand-grouped / numeric-descending order built above.
        const choicesInstance = new (window as any).Choices(devSel, { searchEnabled: devSel.options.length > 10, itemSelectText: '', shouldSort: false });
        // Choices.js doesn't reliably pick up a pre-selected <option>'s `selected` attribute at
        // construction time when it's nested inside an <optgroup> (every device option always
        // is, via the brand-grouping above) — force it to sync its displayed value. This does
        // NOT fire a native change event (see setChoiceByValue's other use in this file), so it
        // won't trigger a redundant reload or write the favPhone cookie for the fallback case.
        if (this.activeDeviceFilter) choicesInstance.setChoiceByValue(this.activeDeviceFilter);
      }
    }

    // Checkboxes
    this.filtersPanel.querySelectorAll<HTMLInputElement>('.bundle-filter__checkbox').forEach((cb) => {
      cb.addEventListener('change', () => {
        if (cb.checked) {
          if (!this.activeFilters.includes(cb.value)) this.activeFilters.push(cb.value);
        } else {
          this.activeFilters = this.activeFilters.filter((f) => f !== cb.value);
        }
        const section = cb.closest<HTMLElement>('.filter__disclosure');
        const activeCount = section?.querySelectorAll<HTMLInputElement>('.bundle-filter__checkbox:checked').length ?? 0;
        this.updateFilterActive(section, activeCount);
        this.updateCollectionsFilterBadges();
        this.updateFilterReset();
        this.loadProductsForType(this.typeSelect.value, true, false);
        trackEvent('bundle_filter_apply', { filter_id: cb.dataset.filterId ?? '', active: cb.checked });
      });
    });

    // Reset button (rendered by JS inside the filter form)
    const resetBtn = this.filtersPanel.querySelector<HTMLButtonElement>('.bundle-tool__filter-reset');
    resetBtn?.addEventListener('click', () => {
      this.activeDeviceFilter = '';
      this.activeFilters = [];
      this.filtersPanel?.querySelectorAll<HTMLInputElement>('.bundle-filter__checkbox').forEach((cb) => { cb.checked = false; });
      const ds = this.filtersPanel?.querySelector<HTMLSelectElement>('.bundle-filter__device');
      if (ds) ds.value = '';
      this.filtersPanel?.querySelectorAll<HTMLElement>('.filter__active').forEach((el) => { el.textContent = ''; });
      if (resetBtn) resetBtn.hidden = true;
      this.loadProductsForType(this.typeSelect.value, true, false);
    });
  }

  private updateFilterActive(section: HTMLElement | null, count: number) {
    const badge = section?.querySelector<HTMLElement>('.filter__active');
    if (!badge) return;
    badge.textContent = count > 0 ? String(count) : '';
  }

  private updateFilterReset() {
    const resetBtn = this.filtersPanel?.querySelector<HTMLButtonElement>('.bundle-tool__filter-reset');
    if (!resetBtn) return;
    resetBtn.hidden = !this.activeDeviceFilter && this.activeFilters.length === 0;
  }

  private closeFiltersDialog() {
    if (!this.filtersDialog) return;
    this.filtersDialog.classList.add('is-closing');
    this.filtersDialog.addEventListener('animationend', () => {
      this.filtersDialog!.classList.remove('is-closing');
      this.filtersDialog!.close();
      document.body.classList.remove('--no-scroll');
    }, { once: true });
  }

  // ─── Load more (manual pagination) ──────────────────────────────────────────

  private handleLoadMoreClick = () => {
    if (!this.loadMoreBtn || this.loadMoreBtn.disabled) return;
    this.loadMoreBtn.disabled = true;
    this.loadMoreBtn.classList.add('button-v2--loading');
    trackEvent('bundle_load_more_products', {
      context: this.searchQuery ? 'search' : 'type_browse',
    });
    if (this.searchQuery) this.loadSearchResults();
    else this.loadProductsForType(this.activeTypeHandle);
  };

  private showLoadMoreButton() {
    if (!this.loadMoreBtn) return;
    this.loadMoreBtn.hidden = false;
    this.loadMoreBtn.disabled = false;
    this.loadMoreBtn.classList.remove('button-v2--loading');
  }

  private hideLoadMoreButton() {
    if (!this.loadMoreBtn) return;
    this.loadMoreBtn.hidden = true;
  }

  // ─── Product loading ──────────────────────────────────────────────────────────

  private getActiveDeviceOption(): { name: string; value: string } | null {
    if (!this.activeDeviceFilter) return null;
    try {
      const parsed = JSON.parse(this.activeDeviceFilter);
      const name = parsed?.variantOption?.name;
      const value = parsed?.variantOption?.value;
      return name && value ? { name, value } : null;
    } catch {
      return null;
    }
  }

  // computePinned gates the "pin matching products first" behavior (see below) to genuine
  // type switches — filter/device changes and the reset button pass `false` here, since
  // pinned matches are fetched unfiltered and would otherwise reappear ignoring whatever
  // filter the user just applied.
  private async loadProductsForType(productType: string, reset = false, computePinned = true) {
    if (!productType) return;
    const loadKey = `type:${productType}`;

    // Pagination (reset=false) must still respect isLoading/hasMore to avoid firing
    // duplicate requests for the same in-progress sequence — but a reset always starts a
    // fresh sequence and must never be silently dropped just because some other mode
    // (e.g. a search that was mid-flight when the user switched types) is still loading.
    if (!reset && (!this.hasMore || this.isLoading || this.loadedCollection !== loadKey)) return;

    if (reset) {
      this.loadToken++;
      this.hideLoadMoreButton();
      this.grid.innerHTML = '';
      if (this.emptyStateEl) this.emptyStateEl.hidden = true;
      this.cursor = null;
      this.hasMore = true;
      this.loadedCollection = loadKey;
      this.filterMetadata = [];
      this.pinnedProductIds = new Set();
    }
    const token = this.loadToken;

    this.isLoading = true;
    this.showLoading();
    if (reset) this.injectSkeletons();

    try {
      const filters = this.buildProductFilters(productType);
      const device = this.getActiveDeviceOption();
      const langCode = this.config.lang.toUpperCase();
      const collectionHandle = PRODUCT_TYPE_ORIGIN_COLLECTION[productType] ?? 'all';

      // Only computed/fired on a fresh type load, never on scroll-triggered pagination —
      // matches the plan's "one extra request per type switch" constraint. Also skipped on
      // filter/device changes (computePinned=false): fetchPinnedMatches queries by title-word
      // match only, ignoring activeFilters/activeDeviceFilter entirely, so re-running it there
      // re-injected products that ignored the filter the user had just applied.
      const distinctiveWords = reset && computePinned ? this.getDistinctiveWords() : new Set<string>();
      const pinnedPromise = reset && computePinned && distinctiveWords.size > 0
        ? this.fetchPinnedMatches(productType, distinctiveWords)
        : Promise.resolve<ShopifyAjaxProduct[]>([]);

      const [json, pinnedProducts] = await Promise.all([
        fetchStorefront(buildBundleGQL(langCode, device?.name, device?.value, collectionHandle), {
          cursor: this.cursor ?? undefined,
          filters,
        }),
        pinnedPromise,
      ]);
      if (token !== this.loadToken) return; // a newer load started while this was in flight

      const payload = json?.data?.collection?.products;
      if (!payload) return;

      if (pinnedProducts.length) {
        this.pinnedProductIds = new Set(pinnedProducts.map((p) => String(p.id)));
        this.appendProductsToGrid(pinnedProducts);
      }

      // On first page load, render filter UI from metadata
      if (!this.cursor && payload.filters?.length) {
        // Normalize filter value inputs to strings — Storefront API may return JSON scalar as object
        this.filterMetadata = payload.filters.map((f: StorefrontFilter) => ({
          ...f,
          values: f.values.map((v: StorefrontFilterValue) => ({
            ...v,
            input: typeof v.input === 'string' ? v.input : JSON.stringify(v.input),
          })),
        }));
        const prevDevice = this.activeDeviceFilter;
        this.renderFilterUI();
        // Cookie auto-applied a device filter — defer reload so isLoading resets first
        if (!prevDevice && this.activeDeviceFilter) {
          setTimeout(() => this.loadProductsForType(productType, true), 0);
          return;
        }
      }

      this.handleProductsPayload(payload);
    } catch {
      // Storefront fetch failed silently
    } finally {
      if (token === this.loadToken) {
        this.isLoading = false;
        this.hideLoading();
        this.clearSkeletons();
      }
    }
  }

  // Free-text search spans every product type (unlike the type dropdown's collection
  // filter), so it bypasses buildProductFilters/collection(handle:"all") entirely.
  private async loadSearchResults(reset = false) {
    const query = this.searchQuery;
    if (!query) return;
    const loadKey = `search:${query}`;

    if (!reset && (!this.hasMore || this.isLoading || this.loadedCollection !== loadKey)) return;

    if (reset) {
      this.loadToken++;
      this.hideLoadMoreButton();
      this.grid.innerHTML = '';
      if (this.emptyStateEl) this.emptyStateEl.hidden = true;
      this.cursor = null;
      this.hasMore = true;
      this.loadedCollection = loadKey;
      // Pinning only applies to type-browsing, not free-text search — clear any
      // stale ids left over from a prior type-browse session so a legitimately
      // matching search result is never filtered out by handleProductsPayload's
      // shared dedupe check.
      this.pinnedProductIds = new Set();
    }
    const token = this.loadToken;

    this.isLoading = true;
    this.showLoading();
    if (reset) this.injectSkeletons();

    try {
      const langCode = this.config.lang.toUpperCase();
      const json = await fetchStorefront(buildBundleSearchGQL(langCode), {
        cursor: this.cursor ?? undefined,
        query: `title:*${sanitizeSearchQuery(query)}*`,
      });
      if (token !== this.loadToken) return; // a newer load started while this was in flight

      const payload = json?.data?.products;
      if (!payload) return;

      this.handleProductsPayload(payload);
      if (reset) {
        const resultsCount = payload.nodes?.length ?? 0;
        trackEvent('bundle_search', { query, results_count: resultsCount });
        if (resultsCount === 0) trackEvent('bundle_search_zero_results', { query });
      }
    } catch {
      // Storefront fetch failed silently
    } finally {
      if (token === this.loadToken) {
        this.isLoading = false;
        this.hideLoading();
        this.clearSkeletons();
      }
    }
  }

  private handleProductsPayload(payload: { nodes?: StorefrontProductNode[]; pageInfo?: { endCursor?: string } }) {
    const nodes: StorefrontProductNode[] = payload.nodes ?? [];
    if (nodes.length < PAGE_SIZE) this.hasMore = false;

    // Pinning only ever applies to type-browsing (loadedCollection starts with
    // "type:") — scoping the check here, not just relying on every caller's reset
    // block to clear pinnedProductIds, means a future caller of this method can't
    // silently inherit the same stale-state bug a prior commit already had to fix.
    const products = nodes
      .map(toAjaxProduct)
      .filter((p) => !this.loadedCollection.startsWith('type:') || !this.pinnedProductIds.has(String(p.id)));
    this.appendProductsToGrid(products);
    this.applyFilters();
    // Re-checks whether the category grid should now take over — needed for the one
    // case where the grid we just populated is for an already-used type (restoring a
    // saved/shared bundle whose first render deliberately shows that type's product
    // grid instead of the category prompt; see isRestoringSelection). Without this,
    // that suppression never gets undone once the real product data finishes loading.
    this.updateTypeLockState();
    this.updateCardStates();

    // Only ever true on a genuinely empty first page — a paginated request
    // (page 2+) always lands on an already-populated grid, so this can't
    // false-positive mid-pagination. Suppressed while the category picker
    // (not the product grid) is what's actually showing.
    if (this.emptyStateEl) {
      this.emptyStateEl.hidden = this.grid.hidden || this.grid.querySelectorAll('.bundle-card').length > 0;
    }

    if (this.hasMore && payload.pageInfo?.endCursor) {
      this.cursor = payload.pageInfo.endCursor;
    } else {
      this.hasMore = false;
    }
    // this.grid.hidden was just decided by updateTypeLockState() above — when the
    // category picker is what's actually showing (the type we fetched turned out
    // to already be used), there's nothing to paginate, so never show the button
    // here regardless of hasMore/cursor — but keep hasMore itself untouched, it
    // still reflects real API pagination state if the product grid reappears later.
    if (this.hasMore && !this.grid.hidden) {
      this.showLoadMoreButton();
    } else {
      this.hideLoadMoreButton();
    }
  }

  private buildProductFilters(productType: string): unknown[] {
    const filters: unknown[] = [{ productType }];
    if (this.activeDeviceFilter) {
      try { filters.push(JSON.parse(this.activeDeviceFilter)); } catch {}
    }
    for (const input of this.activeFilters) {
      try { filters.push(JSON.parse(input)); } catch {}
    }
    return filters;
  }

  // Words to exclude from matching because they're generic to the product type itself
  // ("Case", "Folio", "AirPods"...), not to a specific design. Pulled from the type
  // <select>'s own option labels — already in the shopper's current locale, no extra
  // fetch or schema setting needed.
  private getTypeLabelWords(): Set<string> {
    const words = new Set<string>();
    this.typeSelect.querySelectorAll('option').forEach((opt) => {
      for (const raw of (opt.textContent ?? '').split(/\s+/)) {
        const word = normalizeWord(raw);
        if (word) words.add(word);
      }
    });
    return words;
  }

  // The set of design-specific words across everything already in the bundle (e.g.
  // "Teddy Clear Case" -> {teddy}), used to pin matching products first when browsing
  // a different type. Empty bundle -> empty set -> pinning is skipped entirely.
  private getDistinctiveWords(): Set<string> {
    if (this.selected.length === 0) return new Set();
    const exclude = this.getTypeLabelWords();
    const words = new Set<string>();
    for (const product of this.selected) {
      for (const raw of product.title.split(/\s+/)) {
        const word = normalizeWord(raw);
        if (!word || CONNECTOR_STOPWORDS.has(word) || exclude.has(word)) continue;
        words.add(word);
      }
    }
    return words;
  }

  // One-shot search for products of `productType` whose title contains any of `words` —
  // fired once per fresh type load (never on scroll pagination) alongside the normal
  // paginated query. Reuses the same top-level products(query:) search field the search
  // bar uses, just with an extra product_type: clause baked into the query string.
  private async fetchPinnedMatches(productType: string, words: Set<string>): Promise<ShopifyAjaxProduct[]> {
    const wordClauses = [...words].map((w) => `title:*${sanitizeSearchQuery(w)}*`);
    if (wordClauses.length === 0) return [];

    // sanitizeSearchQuery only escapes double quotes (it's designed for the unquoted
    // title:*...* clauses used elsewhere) — productType is wrapped in single quotes here,
    // so it needs its own escape for that quote character too.
    const safeProductType = sanitizeSearchQuery(productType).replace(/'/g, "\\'");
    const query = `product_type:'${safeProductType}' AND (${wordClauses.join(' OR ')})`;
    const langCode = this.config.lang.toUpperCase();

    try {
      const json = await fetchStorefront(buildBundleSearchGQL(langCode), { query });
      const nodes: StorefrontProductNode[] = json?.data?.products?.nodes ?? [];
      const selectedIds = new Set(this.selected.map((p) => p.productId));
      return nodes
        .map(toAjaxProduct)
        .filter((p) => !selectedIds.has(String(p.id)));
    } catch {
      return [];
    }
  }

  private buildSkeletonHtml(): string {
    const loader = this.config.loaderUrl
      ? `<img class="bundle-card__loader" src="${esc(this.config.loaderUrl)}" alt="" aria-hidden="true" width="75" height="75">`
      : '';
    return `
      <li class="bundle-card bundle-skeleton" aria-hidden="true">
        <div class="bundle-card__inner">
          <div class="bundle-card__media">${loader}</div>
          <div class="bundle-card__body">
            <span class="bundle-skeleton__bar bundle-skeleton__bar--title"></span>
            <span class="bundle-skeleton__bar bundle-skeleton__bar--price"></span>
            <span class="bundle-skeleton__bar bundle-skeleton__bar--options"></span>
          </div>
        </div>
      </li>`;
  }

  private injectSkeletons(count = 6) {
    const html = Array.from({ length: count }, () => this.buildSkeletonHtml()).join('');
    this.grid.insertAdjacentHTML('beforeend', html);
  }

  private clearSkeletons() {
    this.grid.querySelectorAll('.bundle-skeleton').forEach((el) => el.remove());
  }

  private showLoading() {
    if (this.loadingEl) this.loadingEl.hidden = false;
  }

  private hideLoading() {
    if (this.loadingEl) this.loadingEl.hidden = true;
  }

  // ─── Grid rendering ───────────────────────────────────────────────────────────

  private buildCardHtml(product: ShopifyAjaxProduct): string {
    const isSoldOut = !product.variants.some((v) => v.available);
    const defaultVariant = product.variants.find((v) => v.available) ?? product.variants[0];
    if (!defaultVariant) return '';

    const imageSrc = typeof product.featured_image === 'string'
      ? product.featured_image
      : (product.featured_image?.src ?? '');
    const imageUrl = shopifyImageSized(imageSrc, '400x');

    const price        = priceToCents(defaultVariant.price);
    const comparePrice = priceToCents(defaultVariant.compare_at_price);

    const discountPct   = this.getPreviewTier()?.discountPercent ?? 0;
    const discountedCents = Math.round(price * (1 - discountPct / 100));
    const hasDiscount   = discountPct > 0 && price > 0;
    const hasCompare    = comparePrice > price;

    const optionsLabel = this.config.lang === 'fr' ? 'Options du produit' : 'Product options';
    const soldOutLabel  = this.config.lang === 'fr' ? 'Épuisé' : 'Sold out';

    return `
      <li
        class="bundle-card${isSoldOut ? ' is-sold-out' : ''}"
        data-variant-id="${defaultVariant.id}"
        data-product-id="${product.id}"
        data-handle="${esc(product.handle)}"
        data-product-type="${esc(product.product_type)}"
        data-title="${esc(product.title)}"
        data-price="${price}"
        data-compare-at-price="${comparePrice || ''}"
        data-image="${esc(imageUrl)}"
        data-variant-title="${esc(defaultVariant.title)}"
      >
        <button class="bundle-card__inner" type="button" data-action="open" aria-label="${esc(product.title)}"${isSoldOut ? ' disabled aria-disabled="true"' : ''}>
          <div class="bundle-card__media">
            ${imageUrl ? `<img src="${esc(imageUrl)}" alt="${esc(product.title)}" loading="lazy" width="400" height="400">` : ''}
            ${isSoldOut ? `<span class="bundle-card__badge">${esc(soldOutLabel)}</span>` : ''}
            <div class="bundle-card__selected-overlay" aria-hidden="true">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" aria-hidden="true">
                <polyline points="20 6 9 17 4 12"/>
              </svg>
            </div>
          </div>
          <div class="bundle-card__body">
            <p class="bundle-card__title">${esc(product.title)}</p>
            <p class="bundle-card__price">
              <s class="bundle-card__original-price"${!hasDiscount && !hasCompare ? ' hidden' : ''}>
                ${formatMoney(hasCompare ? comparePrice : price, this.config.lang)}
              </s>
              <span class="bundle-card__current-price">${formatMoney(hasDiscount ? discountedCents : price, this.config.lang)}</span>
            </p>
            <span class="bundle-card__options-link">${esc(optionsLabel)}</span>
          </div>
        </button>
      </li>`;
  }

  private appendProductsToGrid(products: ShopifyAjaxProduct[]) {
    const temp = document.createElement('ul');
    temp.innerHTML = products.map((p) => this.buildCardHtml(p)).join('');

    Array.from(temp.querySelectorAll<HTMLElement>('.bundle-card')).forEach((card) => {
      this.grid.appendChild(card);
    });
  }

  private applyFilters() {
    // Search results already come back filtered by the server across every product
    // type — filtering them again by the (possibly unrelated) active type would hide them.
    if (this.searchQuery) return;
    this.grid.querySelectorAll<HTMLElement>('.bundle-card').forEach((card) => {
      card.hidden = !!this.activeTypeHandle && card.dataset.productType !== this.activeTypeHandle;
    });
  }

  private handleSearchChange() {
    if (this.searchQuery) {
      this.cacheBrowseState();
      this.loadSearchResults(true);
    } else if (this.activeTypeHandle) {
      // Cleared the search box — go back to normal per-type browsing
      this.loadProductsForType(this.activeTypeHandle, true);
    }
    this.updateTypeLockState();
  }

  // Called right before a search replaces the grid. Only snapshots while still showing
  // the type-browse view — a no-op on later keystrokes, since by then loadedCollection
  // already points at the search results, not the browse list we want to restore later.
  private cacheBrowseState() {
    if (!this.loadedCollection.startsWith('type:')) return;
    this.hideLoadMoreButton();
    this.browseCache = {
      key: this.loadedCollection,
      html: this.grid.innerHTML,
      cursor: this.cursor,
      hasMore: this.hasMore,
    };
  }

  // Restores the cached browse list when search is closed, avoiding a refetch. Falls back
  // to a fresh load if there's no cache or the active type changed while searching.
  private restoreBrowseState() {
    this.loadToken++;
    const key = `type:${this.activeTypeHandle}`;
    if (this.browseCache && this.browseCache.key === key) {
      this.grid.innerHTML = this.browseCache.html;
      this.cursor = this.browseCache.cursor;
      this.hasMore = this.browseCache.hasMore;
      this.loadedCollection = key;
      this.updateCardStates();
      if (this.hasMore) this.showLoadMoreButton();
    } else {
      this.loadProductsForType(this.activeTypeHandle, true);
    }
    this.updateTypeLockState();
  }

  // ─── Card interactions ────────────────────────────────────────────────────────

  private handleGridClick(e: MouseEvent) {
    const btn = (e.target as HTMLElement).closest<HTMLButtonElement>('[data-action="open"]');
    if (!btn) return;

    const card = btn.closest<HTMLElement>('.bundle-card');
    if (!card) return;

    const device = this.getActiveDeviceOption();

    this.popup.open({
      handle:           card.dataset.handle ?? '',
      title:            card.dataset.title ?? '',
      defaultVariantId: card.dataset.variantId ?? '',
      discountPct:      this.getPreviewTier()?.discountPercent ?? 0,
      isFull:           this.selected.length >= this.config.maxProducts,
      lang:             this.config.lang,
      labels:           this.config.labels,
      deviceOptionName: device?.name,
      deviceValue:      device?.value,
      onAdd:            (product) => this.addProduct(product),
    });
  }

  private addProduct(product: SelectedProduct) {
    if (this.selected.length >= this.config.maxProducts) return;
    if (this.selected.some((p) => p.variantId === product.variantId)) return;
    // Mirrors updateCardStates()'s .is-full (pointer-events: none) lock, which
    // is CSS-only and doesn't remove the card's button from the tab order —
    // this is the real guard a keyboard user (Tab + Enter) can't bypass.
    if (this.selected.some((p) => p.productType === product.productType)) return;

    const previousTier = this.getCurrentTier();
    this.selected.push(product);
    trackEvent('bundle_product_add', {
      product_type: product.productType,
      slot_position: this.selected.length,
    });
    const newTier = this.getCurrentTier();
    if (newTier && newTier.minQty !== previousTier?.minQty) {
      trackEvent('bundle_discount_tier_reached', {
        discount_percent: newTier.discountPercent,
        product_count: this.selected.length,
      });
    }
    // Search results span every type (unlike the type <select>'s own collection
    // filter), so a product added from search can belong to a different type than
    // whatever's currently active. Without re-syncing here, activeTypeHandle/the
    // <select> stay stuck on the old type and updateTypeLockState()'s
    // showCategoryGrid check (which only looks at the active type, not every used
    // type) never re-shows the category picker — so the just-used type's card
    // never visibly renders as disabled until the shopper manually reselects it.
    if (this.activeTypeHandle !== product.productType) {
      this.activeTypeHandle = product.productType;
      this.setTypeSelectValue(product.productType);
    }
    this.render();
    this.syncURL();

    // On mobile the bundle-bar panel is a collapsed strip until the shopper taps it
    // open — surface it automatically once the last slot is filled so they see their
    // finished bundle instead of a shrunk, easy-to-miss strip. Desktop's panel is an
    // always-open sticky sidebar already, so opening it there would only trigger the
    // panel's mobile-only backdrop/body-scroll-lock side effects for no visual gain.
    if (this.selected.length >= this.config.maxProducts && this.isMobileViewport()) {
      this.openPanel();
    }
  }

  private isMobileViewport(): boolean {
    return window.matchMedia('(max-width: 989px)').matches;
  }

  private removeProduct(variantId: string) {
    this.selected = this.selected.filter((p) => p.variantId !== variantId);
    this.render();
    this.syncURL();
  }

  // ─── Derived state ──────────────────────────────────────────────────────────

  private getCurrentTier(): BundleTier | null {
    const count = this.selected.length;
    return this.config.tiers
      .filter((t) => count >= t.minQty)
      .sort((a, b) => b.minQty - a.minQty)[0] ?? null;
  }

  private getNextTier(): BundleTier | null {
    const count = this.selected.length;
    return this.config.tiers
      .filter((t) => count < t.minQty)
      .sort((a, b) => a.minQty - b.minQty)[0] ?? null;
  }

  private getMinTier(): BundleTier | null {
    return this.config.tiers.slice().sort((a, b) => a.minQty - b.minQty)[0] ?? null;
  }

  // Discount shown on product cards/popup: the tier actually earned, or the
  // first tier as a preview when nothing qualifies for a discount yet.
  private getPreviewTier(): BundleTier | null {
    return this.getCurrentTier() ?? this.getMinTier();
  }

  // ─── Render ─────────────────────────────────────────────────────────────────

  private render() {
    this.updateTypeLockState();
    this.updateCardStates();
    this.updateCardPrices();
    this.renderBarSlots();
    this.renderPanelItems();
    this.updateBarSummary();
    this.updateButtons();
    // Keep popup CTA in sync if open
    this.popup?.updateDiscountPct(this.getPreviewTier()?.discountPercent ?? 0);
  }

  // Locks each product type option to one use: once a product of that type is in the
  // bundle, its <option> is disabled and the grid is swapped for the "pick another
  // category" prompt until a different, still-available type is chosen.
  private updateTypeLockState() {
    const usedTypes = new Set(this.selected.map((p) => p.productType));
    const isFull = this.selected.length >= this.config.maxProducts;

    // Dims a category card and swaps its CTA label once its product type has
    // already had a product added to the bundle — purely visual, no click-blocking
    // beyond the .is-used CSS's pointer-events (selectType() is idempotent anyway).
    // Once the bundle itself is full, every remaining card locks too (.is-full),
    // regardless of type.
    this.categoryGridEl?.querySelectorAll<HTMLButtonElement>('.bundle-tool__category-card-cta[data-select-type]').forEach((btn) => {
      const handle = btn.dataset.selectType ?? '';
      const isUsed = usedTypes.has(handle);
      const card = btn.closest<HTMLElement>('.bundle-tool__category-card');
      card?.classList.toggle('is-used', isUsed);
      card?.classList.toggle('is-full', !isUsed && isFull);
      btn.textContent = isUsed
        ? (this.config.labels.alreadySelected ?? 'Already selected')
        : (this.config.labels.startCreating ?? 'Start creating');
    });

    const alreadySelectedSuffix = ` (${this.config.labels.alreadySelected ?? 'Already selected'})`;
    if (this.typeSelectChoices) {
      // Choices.js keeps its own internal choice store — writing opt.disabled/
      // textContent straight onto the native <option>s (the pre-Choices approach)
      // isn't reflected in its rendered dropdown. A full rebuild from the
      // Liquid-rendered base list (this.typeOptionsBase) is the supported way to
      // change disabled state/labels after init — see updateCaseTypeOptions() in
      // product-variant.ts for the same pattern.
      this.typeSelectChoices.setChoices(
        this.typeOptionsBase.map(({ value, label }) => {
          const isUsed = Boolean(value) && usedTypes.has(value);
          return {
            value,
            label: isUsed ? `${label}${alreadySelectedSuffix}` : label,
            disabled: isUsed,
            selected: value === this.activeTypeHandle,
          };
        }),
        'value',
        'label',
        true,
      );
    } else {
      Array.from(this.typeSelect.options).forEach((opt) => {
        if (!opt.value) return;
        const isUsed = usedTypes.has(opt.value);
        opt.disabled = isUsed;
        // Preserve the original label so it can be restored once the type frees up
        // again (e.g. the product is removed from the bundle).
        if (opt.dataset.baseLabel === undefined) opt.dataset.baseLabel = opt.textContent ?? '';
        opt.textContent = isUsed ? `${opt.dataset.baseLabel}${alreadySelectedSuffix}` : opt.dataset.baseLabel;
      });
    }

    // Shows the category-card grid both on fresh arrival (no type chosen yet)
    // and once the active type has already had a product added ("pick another
    // category"). A search spans every type, so its results should stay
    // visible even if the dropdown's active type is otherwise locked out.
    const showCategoryGrid = !this.searchQuery && !this.isRestoringSelection && (!this.activeTypeHandle || usedTypes.has(this.activeTypeHandle));
    this.grid.hidden = showCategoryGrid;
    if (this.categoryGridEl) this.categoryGridEl.hidden = !showCategoryGrid;
    // The category picker has nothing to paginate — without this, the product
    // grid's own Load more button stayed visible (and clickable) underneath it,
    // orphaned from the grid it belongs to.
    if (showCategoryGrid) this.hideLoadMoreButton();
    // Filters are scoped to whatever type's grid is currently showing — nothing
    // to filter while the category picker is up instead (no type in progress).
    if (this.filterBtn) this.filterBtn.disabled = showCategoryGrid;
  }

  private updateCardStates() {
    const selectedVariants = new Set(this.selected.map((p) => p.variantId));
    const usedTypes = new Set(this.selected.map((p) => p.productType));
    const isFull = this.selected.length >= this.config.maxProducts;

    this.grid.querySelectorAll<HTMLElement>('.bundle-card').forEach((card) => {
      const isSelected = selectedVariants.has(card.dataset.variantId ?? '');
      // Search results span every type, including ones already used — the normal
      // per-type grid never shows these since it's replaced by the category prompt instead.
      const typeUsed = usedTypes.has(card.dataset.productType ?? '');
      card.classList.toggle('is-selected', isSelected);
      // Already-selected cards get the same disabled look as locked-out ones — see
      // the .is-selected/.is-full comment in section-bundle-tool.scss for why.
      card.classList.toggle('is-full', isSelected || isFull || typeUsed);

      const optionsLink = card.querySelector<HTMLElement>('.bundle-card__options-link');
      if (optionsLink && isSelected) {
        optionsLink.textContent = this.config.lang === 'fr' ? 'Sélectionné ✓' : 'Selected ✓';
      }
    });
  }

  private updateCardPrices() {
    const discountPct = this.getPreviewTier()?.discountPercent ?? 0;

    this.grid.querySelectorAll<HTMLElement>('.bundle-card').forEach((card) => {
      const originalEl  = card.querySelector<HTMLElement>('.bundle-card__original-price');
      const discountedEl = card.querySelector<HTMLElement>('.bundle-card__current-price');
      const priceCents   = parseInt(card.dataset.price ?? '0');
      if (!originalEl || !discountedEl || !priceCents) return;

      const discountedCents = Math.round(priceCents * (1 - discountPct / 100));

      if (discountPct > 0) {
        originalEl.hidden = false;
        originalEl.textContent = formatMoney(priceCents, this.config.lang);
        discountedEl.textContent = formatMoney(discountedCents, this.config.lang);
      } else {
        originalEl.hidden = true;
        discountedEl.textContent = formatMoney(priceCents, this.config.lang);
      }
    });
  }

  private renderBarSlots() {
    const { maxProducts } = this.config;
    this.barSlotRow.innerHTML = Array.from({ length: maxProducts }, (_, i) => {
      const product = this.selected[i];
      if (product) {
        return `
          <div class="bundle-bar__slot bundle-bar__slot--filled">
            ${product.image
              ? `<img src="${esc(product.image)}" alt="${esc(product.title)}" width="48" height="48" loading="lazy">`
              : '<div class="bundle-bar__slot-placeholder"></div>'
            }
          </div>`;
      }
      return `
        <div class="bundle-bar__slot bundle-bar__slot--empty">
          <svg width="14" height="14" viewBox="0 0 14 14" fill="none" aria-hidden="true">
            <path d="M7 1V13M1 7H13" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/>
          </svg>
        </div>`;
    }).join('');
  }

  // Empty-slot promo labels are one merchant-configured string (e.g.
  // "Add your Second - Save 15%"), not two separate fields — so bolding just the
  // trailing "Save X%" clause has to be done by splitting on the last " - "
  // occurrence in code. Falls back to plain escaped text when no separator is
  // present (e.g. the slot-1 label, which has no discount clause to bold).
  private renderAddLabel(label: string): string {
    const separator = ' - ';
    const idx = label.lastIndexOf(separator);
    if (idx === -1) return esc(label);
    const lead = label.slice(0, idx);
    const bold = label.slice(idx + separator.length);
    return `${esc(lead)}${esc(separator)}<strong>${esc(bold)}</strong>`;
  }

  private renderPanelItems() {
    const labelModify  = this.config.labels.modify ?? 'Modify';
    const labelDelete  = this.config.labels.delete ?? 'Delete';
    // Preview tier, not just the one actually reached — shoppers should see the
    // minimum discount they're guaranteed once the bundle qualifies, even below minQty.
    const discountPct  = this.getPreviewTier()?.discountPercent ?? 0;
    const { maxProducts, tiers } = this.config;

    // Always render maxProducts rows — filled with the selected product for that
    // slot, or an empty "+"/promo-label placeholder per the Figma sidebar mockup —
    // instead of only rendering rows for products already added.
    this.barPanelItems.innerHTML = Array.from({ length: maxProducts }, (_, i) => {
      const slotNumber = i + 1;
      const product = this.selected[i];

      if (product) {
        const discountedCents = Math.round(product.price * (1 - discountPct / 100));
        const showOriginal = discountPct > 0 && product.price > 0;

        return `
        <li class="bundle-bar__item bundle-bar__item--filled">
          <div class="bundle-bar__item-card">
            <span class="bundle-bar__item-number">${slotNumber}</span>
            <div class="bundle-bar__item-media">
              ${product.image
                ? `<img src="${esc(product.image)}" alt="${esc(product.title)}" width="64" height="64" loading="lazy">`
                : '<div class="bundle-bar__item-placeholder"></div>'
              }
            </div>
            <div class="bundle-bar__item-info">
              <div class="bundle-bar__item-header">
                <p class="bundle-bar__item-title">${esc(product.title)}</p>
                <button class="bundle-bar__item-edit" type="button" data-action="modify" data-variant-id="${esc(product.variantId)}">
                  ${esc(labelModify)}
                </button>
              </div>
              ${product.productType ? `<p class="bundle-bar__item-type">${esc(product.productType)}</p>` : ''}
              ${product.variantTitle ? `<p class="bundle-bar__item-variant">${esc(product.variantTitle)}</p>` : ''}
              <p class="bundle-bar__item-price">
                ${showOriginal ? `<s class="bundle-bar__item-original-price">${formatMoney(product.price, this.config.lang)}</s>` : ''}
                <strong>${formatMoney(showOriginal ? discountedCents : product.price, this.config.lang)}</strong>
                ${showOriginal ? `<span class="bundle-bar__item-discount-badge">${discountPct}%</span>` : ''}
              </p>
            </div>
            <button class="bundle-bar__item-remove" type="button" data-action="delete" data-variant-id="${esc(product.variantId)}" aria-label="${esc(labelDelete)}">
              <svg width="10" height="10" viewBox="0 0 10 10" fill="none" aria-hidden="true">
                <path d="M1 1L9 9M9 1L1 9" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/>
              </svg>
            </button>
          </div>
        </li>`;
      }

      // Empty slot: "+" placeholder with a promo label — the first slot (no
      // discount tier) uses the section-level addFirstLabel, slots 2+ use their
      // per-tier promo label (merchant-configured, not auto-generated from the
      // slot number). The whole card is a button reproducing what clicking
      // bundle-tool__type-select does (openTypeSelect(): closes the panel and
      // opens the native select, showing every type with already-used ones
      // disabled) — see openTypeSelect() for that shared behavior.
      const label = slotNumber === 1
        ? this.config.labels.addFirstLabel
        : tiers.find((t) => t.minQty === slotNumber)?.addSlotLabel;
      const isLastSlot = slotNumber === maxProducts;
      return `
      <li class="bundle-bar__item bundle-bar__item--empty${isLastSlot ? ' bundle-bar__item--highlight' : ''}">
        <button type="button" class="bundle-bar__item-card" data-action="add-slot" aria-label="${esc(this.config.labels.selectType ?? '')}">
          <span class="bundle-bar__item-number">${slotNumber}</span>
          <div class="bundle-bar__item-add-icon" aria-hidden="true">
            <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
              <path d="M6 1V11M1 6H11" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/>
            </svg>
          </div>
          ${label ? `<p class="bundle-bar__item-add-label">${this.renderAddLabel(label)}</p>` : ''}
        </button>
      </li>`;
    }).join('');

    this.barPanelItems.querySelectorAll<HTMLButtonElement>('[data-action="delete"]').forEach((btn) => {
      btn.addEventListener('click', () => this.removeProduct(btn.dataset.variantId!));
    });
    this.barPanelItems.querySelectorAll<HTMLButtonElement>('[data-action="modify"]').forEach((btn) => {
      btn.addEventListener('click', () => this.openModifyPopup(btn.dataset.variantId!));
    });
    this.barPanelItems.querySelectorAll<HTMLButtonElement>('[data-action="add-slot"]').forEach((btn) => {
      btn.addEventListener('click', () => this.openTypeSelect());
    });
  }

  private openModifyPopup(variantId: string) {
    const product = this.selected.find((p) => p.variantId === variantId);
    if (!product) return;

    this.popup.open({
      handle:           product.handle,
      title:            product.title,
      defaultVariantId: product.variantId,
      discountPct:      this.getPreviewTier()?.discountPercent ?? 0,
      isFull:           false,
      lang:             this.config.lang,
      labels:           this.config.labels,
      onAdd:            (next) => this.replaceProduct(variantId, next),
    });
  }

  private replaceProduct(variantId: string, next: SelectedProduct) {
    // Swapping to a variant already used by another slot would create a duplicate —
    // ignore the swap rather than let two slots hold the same variant.
    if (this.selected.some((p) => p.variantId === next.variantId && p.variantId !== variantId)) return;

    this.selected = this.selected.map((p) => (p.variantId === variantId ? next : p));
    this.render();
    this.syncURL();
  }

  private updateBarSummary() {
    // Actual earned tier, not the preview — this bar shows the real total the
    // shopper will pay, so the discount can't show before it's actually earned.
    const tier         = this.getCurrentTier();
    const totalCents   = this.selected.reduce((sum, p) => sum + p.price, 0);
    const discountPct  = tier?.discountPercent ?? 0;
    const discountedCents = Math.round(totalCents * (1 - discountPct / 100));

    const showOriginal = discountPct > 0 && totalCents > 0;
    this.barOriginalPrice.hidden       = !showOriginal;
    this.barOriginalPrice.textContent  = showOriginal ? formatMoney(totalCents, this.config.lang) : '';
    this.barDiscountedPrice.textContent = totalCents > 0 ? formatMoney(discountedCents, this.config.lang) : '';

    this.totalRow.hidden = discountPct <= 0;
    this.barDiscountBadge.hidden = discountPct <= 0;
    if (discountPct > 0) {
      this.barDiscountBadge.textContent = `${discountPct}%`;
    }

    this.updateRewardMentions(totalCents > 0 ? discountedCents : 0);
  }

  // Used by updateButtons()'s disabled-cart-button nudge: given how many products
  // are still missing to unlock the next tier and its discount percent, returns
  // the localized "Add X more product(s) to get Y% off" copy.
  private formatAddForDiscount(missingCount: number, discountPercent: number): string {
    const fallback = this.config.lang === 'fr'
      ? 'Ajoutez {count} produit(s) de plus pour obtenir {n}% de rabais'
      : 'Add {count} more product(s) to get {n}% off';
    return (this.config.labels.addForDiscount || fallback)
      .replace('{count}', String(missingCount))
      .replace('{n}', String(discountPercent));
  }

  // Mirrors the points formula in snippets/product-side-points.liquid, applied to the
  // bundle's live discounted total instead of a single product's static price.
  private updateRewardMentions(chargeCents: number) {
    if (!this.barMentions.length) return;

    const dollars = Math.ceil(chargeCents / 100);
    let points = dollars * 10;
    if (this.config.isVip) points *= 12.5;
    const pointsStrong = `<strong>${points} points.</strong> <br>`;

    this.barMentions.forEach((mention) => {
      const heading = mention.querySelector('h5');
      const template = mention.dataset.lienTemplate ?? '';
      if (heading) heading.innerHTML = template.replace('[points]', pointsStrong);
    });
  }

  private updateButtons() {
    const minTier = this.getMinTier();
    const isReady = minTier ? this.selected.length >= minTier.minQty : false;
    const isEmpty = this.selected.length === 0;

    // Add to cart implies a real purchase at the tier's discount, so it still
    // requires the minimum. Save just persists a work-in-progress bundle, so
    // it only needs at least one product picked.
    // Whenever it's disabled for sitting below that minimum (0 selected, 1
    // selected, however many are still short), swap its label for a nudge
    // naming how many products are missing and the discount they'd unlock,
    // instead of a plain "Add to cart" that gives no reason it's greyed out.
    const nextTier = this.getNextTier();
    const missing = nextTier ? nextTier.minQty - this.selected.length : 0;
    const showDiscountNudge = !isReady && missing > 0;
    const cartLabel = showDiscountNudge
      ? this.formatAddForDiscount(missing, nextTier!.discountPercent)
      : (this.config.labels.addToCart ?? 'Add to cart');
    this.cartBtns.forEach((btn) => {
      btn.disabled = !isReady;
      btn.textContent = cartLabel;
    });
    this.saveBtns.forEach((btn) => { btn.disabled = isEmpty; });

    // Nothing to show or open with an empty bundle — keep the strip inert.
    this.barExpandBtn.disabled = isEmpty;
    if (isEmpty && this.panelOpen) this.closePanel();
  }

  // ─── Panel open / close ─────────────────────────────────────────────────────

  private openPanel() {
    if (this.panelOpen) return;
    this.panelOpen = true;
    this.barPanel.hidden = false;
    this.barBackdrop.hidden = false;
    this.barStripSubtitle.hidden = true;
    this.barSlotRow.hidden = true;
    this.barExpandBtn.setAttribute('aria-expanded', 'true');
    document.body.classList.add('bundle-panel-open');
  }

  private closePanel() {
    if (!this.panelOpen) return;
    this.panelOpen = false;
    this.barPanel.hidden = true;
    this.barBackdrop.hidden = true;
    this.barStripSubtitle.hidden = false;
    this.barSlotRow.hidden = false;
    this.barExpandBtn.setAttribute('aria-expanded', 'false');
    document.body.classList.remove('bundle-panel-open');
  }

  // Triggered by an empty bundle-bar__item slot — closes the panel and sends
  // the shopper back to the type selector to pick a product for that slot,
  // opening its dropdown so they can pick without an extra click. Already-used
  // types are disabled and relabeled by updateTypeLockState().
  //
  // Native <select>.showPicker() has no Safari support at all, desktop or iOS
  // (confirmed live, and caniuse shows it unsupported in every Safari version
  // including Technology Preview) — with no scripted fallback either, since a
  // synthetic click/focus never opens a native select's dropdown, only a real
  // user click or showPicker() do. That's why typeSelect runs through Choices.js
  // (see bindDOM()): its showDropdown() opens a self-rendered dropdown, not the
  // browser's native UI, so it isn't gated by that platform limitation.
  private openTypeSelect() {
    this.closePanel();
    const scrollTarget: HTMLElement = this.typeSelectChoices?.containerOuter?.element ?? this.typeSelect;
    scrollTarget.scrollIntoView({ behavior: 'smooth', block: 'center' });
    if (this.typeSelectChoices) {
      this.typeSelectChoices.showDropdown();
      return;
    }
    // No Choices instance (lib failed to load) — fall back to the native select.
    const picker = this.typeSelect as HTMLSelectElement & { showPicker?: () => void };
    if (typeof picker.showPicker === 'function') {
      try { picker.showPicker(); return; } catch { /* fall through to focus() */ }
    }
    this.typeSelect.focus();
  }

  // ─── URL sync ───────────────────────────────────────────────────────────────

  private syncURL() {
    writeStateToURL({
      products: this.selected.map((p) => ({ handle: p.handle, variantId: p.variantId })),
    });
  }

  // ─── Save ───────────────────────────────────────────────────────────────────

  private async handleSave(): Promise<boolean> {
    // Saving doesn't require the tier minimum — a work-in-progress bundle with
    // just one product can be saved. Add to cart is what enforces the minimum,
    // since only that path implies a real purchase at a tier's discount.
    const minTier = this.getMinTier();
    if (!minTier || this.selected.length === 0) return false;
    if (!this.nameDialog) return false;

    const defaultName = this.savedBundleId
      ? readSavedBundles().find((b) => b.id === this.savedBundleId)?.name
        ?? (this.config.lang === 'fr' ? 'Mon bundle' : 'My bundle')
      : (this.config.lang === 'fr' ? 'Mon bundle' : 'My bundle');

    const name = await this.nameDialog.prompt(defaultName);
    if (name === null) return false;

    const id = this.savedBundleId ?? generateBundleId();

    const saved = saveBundleToStorage({
      id,
      name,
      state: {
        type:             '',
        name:             '',
        typeName:         this.incomingTypeName || this.config.labels.customBundleType,
        differentTypes:   false,
        minProducts:      minTier.minQty,
        maxProducts:      this.config.maxProducts,
        discountPercent:  this.getCurrentTier()?.discountPercent ?? 0,
        collectionHandle: this.config.collectionHandle,
        products:         [...this.selected],
      },
      savedAt: Date.now(),
    });

    if (!saved) {
      showToast(this.config.lang === 'fr' ? "Impossible d'enregistrer le bundle" : 'Could not save bundle');
      return false;
    }

    this.savedBundleId = id;
    this.barStripTitleText.textContent = name;
    if (this.breadcrumbCurrent) this.breadcrumbCurrent.textContent = name;
    if (this.panelTitle) this.panelTitle.textContent = name;
    showToast(this.config.lang === 'fr' ? 'Bundle sauvegardé !' : 'Bundle saved!');
    trackEvent('bundle_save', {
      product_count: this.selected.length,
      discount_percent: this.getCurrentTier()?.discountPercent ?? 0,
    });
    return true;
  }

  // A cart line is identified by its variant + properties, so there's no API
  // call that "updates" one in place — this drops every line still carrying
  // the edited group's id before handleAddToCart() adds the new selection,
  // so editing a bundle replaces it in the cart instead of stacking a second
  // copy alongside the original. Best-effort: a failed removal still lets the
  // add through rather than blocking the shopper's primary action, at the
  // cost of leaving the old lines behind for them to remove manually.
  private async removeCartBundleGroup(bundleGroupId: string): Promise<void> {
    try {
      const cart = await (await fetch('/cart.js')).json();
      const updates: number[] = cart.items.map((item: any) =>
        item.properties?._bundle_group === bundleGroupId ? 0 : item.quantity
      );
      await fetch('/cart/update.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ updates }),
      });
    } catch (e) {
      console.error('[bundle-tool] failed to remove previous bundle from cart:', e);
    }
  }

  private async handleAddToCart() {
    // Add to cart enforces the tier minimum (see handleSave()'s comment on
    // why Save doesn't) — updateButtons() already keeps cartBtns disabled
    // until this.selected.length reaches the minimum tier's minQty, so by
    // the time this fires the gate is already satisfied.
    if (this.selected.length === 0 || this.cartBtns.some((btn) => btn.disabled)) return;

    const discountPercent = this.getCurrentTier()?.discountPercent ?? 0;
    const savedName = this.savedBundleId
      ? readSavedBundles().find((b) => b.id === this.savedBundleId)?.name
      : undefined;
    const bundleName = savedName || this.incomingTypeName || this.config.labels.customBundleType;

    this.cartBtns.forEach((btn) => { btn.disabled = true; });
    if (this.editingCartGroupId) await this.removeCartBundleGroup(this.editingCartGroupId);
    const added = await addBundleToCart(
      this.selected.map((p) => ({ variantId: p.variantId })),
      {
        discountPercent,
        bundleName,
        isFr: this.config.lang === 'fr',
        showToast,
        source: 'bundle-tool',
      },
    );
    if (!added) this.updateButtons();

    if (added) {
      trackEvent('bundle_add_to_cart', {
        product_count: this.selected.length,
        discount_percent: discountPercent,
        source: 'builder',
      });
      if (this.savedBundleId) deleteBundleFromStorage(this.savedBundleId);
      this.resetBuilder();
    }
  }

  // Runs after a successful add-to-cart. The cart drawer already opens in place
  // (bundle-cart-add.ts's renderContents call) — there's no page reload anymore
  // to fall back on for clearing the builder, so it's done explicitly here.
  // Without this, the selection/cart buttons would stay populated and enabled,
  // and a second click would add the same bundle to the cart again.
  private resetBuilder() {
    this.selected = [];
    this.savedBundleId = null;
    this.editingCartGroupId = null;
    this.incomingTypeName = '';
    this.activeTypeHandle = '';
    this.setTypeSelectValue('');
    this.closePanel();
    this.barStripTitleText.textContent = this.defaultBundleTitle;
    if (this.breadcrumbCurrent) this.breadcrumbCurrent.textContent = this.defaultBreadcrumbTitle;
    if (this.panelTitle) this.panelTitle.textContent = this.defaultBundleTitle;
    this.render();
    window.history.replaceState(null, '', window.location.pathname);
  }
}

// Initialise once DOM is ready (script is deferred so DOM is already parsed)
new BundlePage().init();
